import { BarChart3, Calendar, Loader, Receipt, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './Informes.css';

const Informes = () => {
  const { authenticatedFetch } = useAuth();
  const [zDate, setZDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [zSource, setZSource] = useState('COMPARE'); // DB | QUIPU | COMPARE

  const [dbZData, setDbZData] = useState(null);
  const [quipuZData, setQuipuZData] = useState(null);
  const [zLoading, setZLoading] = useState(false);
  const [zError, setZError] = useState(null);

  const [dbHistory, setDbHistory] = useState([]);
  const [quipuHistory, setQuipuHistory] = useState([]);

  const fetchZDiaria = useCallback(async () => {
    setZLoading(true);
    setZError(null);
    try {
      const base = buildApiUrl(API_ENDPOINTS.FACTURACION);

      const dbUrl = `${base}/z?date=${zDate}`;
      const quipuUrl = `${base}/quipu/z?date=${zDate}`;

      const historyDbUrl = `${base}/payments?dateFrom=${zDate}&dateTo=${zDate}&page=1&limit=20`;

      const needsDb = zSource === 'DB' || zSource === 'COMPARE';
      const needsQuipu = zSource === 'QUIPU' || zSource === 'COMPARE';

      const [
        dbRes,
        quipuRes,
        dbHistRes,
      ] = await Promise.all([
        needsDb ? authenticatedFetch(dbUrl) : Promise.resolve(null),
        needsQuipu ? authenticatedFetch(quipuUrl) : Promise.resolve(null),
        needsDb ? authenticatedFetch(historyDbUrl) : Promise.resolve(null),
      ]);

      const [
        dbJson,
        quipuJson,
        dbHistJson,
      ] = await Promise.all([
        dbRes ? dbRes.json().catch(() => null) : Promise.resolve(null),
        quipuRes ? quipuRes.json().catch(() => null) : Promise.resolve(null),
        dbHistRes ? dbHistRes.json().catch(() => null) : Promise.resolve(null),
      ]);

      if (dbRes && !dbRes.ok) throw new Error(dbJson?.message || 'Error al cargar la Z (DB)');
      if (quipuRes && !quipuRes.ok) throw new Error(quipuJson?.message || 'Error al cargar la Z (Quipu)');

      setDbZData(dbJson);
      setQuipuZData(quipuJson);

      // Historial DB: /payments devuelve { items, pagination } (ver backend)
      setDbHistory(Array.isArray(dbHistJson?.items) ? dbHistJson.items : []);
      // Historial Quipu: reutilizar la lista de documentos ya incluida en /quipu/z (evita otra llamada + rate limit)
      setQuipuHistory(Array.isArray(quipuJson?.documents) ? quipuJson.documents : []);
    } catch (err) {
      setZError(err.message || 'Error de conexión');
      setDbZData(null);
      setQuipuZData(null);
      setDbHistory([]);
      setQuipuHistory([]);
    } finally {
      setZLoading(false);
    }
  }, [zDate, authenticatedFetch, zSource]);

  useEffect(() => {
    fetchZDiaria();
  }, [fetchZDiaria]);

  const paymentMethodLabelsDb = {
    STRIPE: 'Stripe / Tarjeta',
    TPV: 'TPV',
    CASH: 'Efectivo',
    BIZUM: 'Bizum',
    BANK_TRANSFER: 'Transferencia',
    AGENCY: 'Agencia',
    NONE: 'Sin método',
  };

  const paymentMethodLabelsQuipu = {
    bank_card: 'Tarjeta',
    cash: 'Efectivo',
    bank_transfer: 'Transferencia',
    direct_debit: 'Domiciliación',
    paypal: 'PayPal',
    check: 'Cheque',
    factoring: 'Factoring',
    unknown: 'Desconocido',
  };

  const paymentMethodLabels = {
    STRIPE: 'Stripe / Tarjeta',
    TPV: 'TPV',
    CASH: 'Efectivo',
    BIZUM: 'Bizum',
    BANK_TRANSFER: 'Transferencia',
    AGENCY: 'Agencia',
    NONE: 'Sin método',
  };

  const activityLabels = {
    BIKE_RENTAL: 'Alquiler',
    INSURANCE: 'Seguros',
    EXTRA: 'Extras',
    TOUR: 'Tours',
    ACCESSORIES: 'Accesorios',
    DELIVERY: 'Entrega',
    OTHER: 'Otros',
  };

  const renderZ = (zData, opts = {}) => {
    const {
      paymentLabels = paymentMethodLabels,
      titleSuffix = '',
    } = opts;

    if (!zData) return null;

    return (
      <div className="z-content">
        <div className="z-total">
          <span className="z-total-label">Total del día{titleSuffix ? ` · ${titleSuffix}` : ''}</span>
          <span className="z-total-value">{zData.total}</span>
          <span className="z-total-movements">{zData.movementCount} movimientos</span>
        </div>

        <div className="z-grid">
          <div className="z-card">
            <h3>Por forma de pago</h3>
            <ul className="z-list">
              {Object.entries(zData.byPaymentMethod || {}).map(([key, value]) => (
                <li key={key}>
                  <span className="z-label">{paymentLabels[key] || key}</span>
                  <span className="z-amount">{value}</span>
                </li>
              ))}
              {Object.keys(zData.byPaymentMethod || {}).length === 0 && (
                <li className="z-empty">Sin movimientos</li>
              )}
            </ul>
          </div>

          <div className="z-card">
            <h3>Por tipo de actividad</h3>
            <ul className="z-list">
              {Object.entries(zData.byActivity || {}).map(([key, value]) => (
                <li key={key}>
                  <span className="z-label">{activityLabels[key] || key}</span>
                  <span className="z-amount">{value}</span>
                </li>
              ))}
              {Object.keys(zData.byActivity || {}).length === 0 && (
                <li className="z-empty">Sin movimientos</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const totalDelta = useMemo(() => {
    if (!dbZData?.total || !quipuZData?.total) return null;
    const parseEur = (s) => Number(String(s).replace('€', '').replace(',', '.').trim());
    const a = parseEur(dbZData.total);
    const b = parseEur(quipuZData.total);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    const diff = Math.round((b - a) * 100) / 100;
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff.toFixed(2)} € (Quipu - DB)`;
  }, [dbZData, quipuZData]);

  return (
    <div className="content-page informes-page">
      <div className="page-header">
        <h1>Informes y contabilidad</h1>
      </div>

      {/* Z diaria */}
      <section className="informes-section z-diaria">
        <div className="section-header">
          <h2>
            <Receipt size={20} />
            Z diaria (cierre diario)
          </h2>
          <div className="z-date-control">
            <label htmlFor="z-date">Fecha:</label>
            <input
              id="z-date"
              type="date"
              value={zDate}
              onChange={(e) => setZDate(e.target.value)}
            />
            <div className="z-source-toggle" role="group" aria-label="Fuente de Z diaria">
              <button
                type="button"
                className={`btn-secondary ${zSource === 'DB' ? 'active' : ''}`}
                onClick={() => setZSource('DB')}
                disabled={zLoading}
              >
                DB
              </button>
              <button
                type="button"
                className={`btn-secondary ${zSource === 'QUIPU' ? 'active' : ''}`}
                onClick={() => setZSource('QUIPU')}
                disabled={zLoading}
              >
                Quipu
              </button>
              <button
                type="button"
                className={`btn-secondary ${zSource === 'COMPARE' ? 'active' : ''}`}
                onClick={() => setZSource('COMPARE')}
                disabled={zLoading}
              >
                Comparar
              </button>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={fetchZDiaria}
              disabled={zLoading}
            >
              {zLoading ? 'Cargando...' : 'Actualizar'}
            </button>
          </div>
        </div>

        {zLoading && (
          <div className="informes-loading">
            <Loader size={24} className="spin" />
            <p>Cargando cierre del día...</p>
          </div>
        )}

        {zError && (
          <div className="informes-error">
            <p>{zError}</p>
          </div>
        )}

        {!zLoading && !zError && (zSource === 'DB') && renderZ(dbZData, { paymentLabels: paymentMethodLabelsDb, titleSuffix: 'DB' })}
        {!zLoading && !zError && (zSource === 'QUIPU') && renderZ(quipuZData, { paymentLabels: paymentMethodLabelsQuipu, titleSuffix: 'Quipu' })}

        {!zLoading && !zError && (zSource === 'COMPARE') && (
          <div className="z-compare">
            <div className="z-compare-header">
              <div className="z-compare-delta">
                <strong>Diferencia total:</strong> {totalDelta || '—'}
              </div>
            </div>
            <div className="z-compare-grid">
              <div className="z-compare-col">
                {renderZ(dbZData, { paymentLabels: paymentMethodLabelsDb, titleSuffix: 'DB' })}
              </div>
              <div className="z-compare-col">
                {renderZ(quipuZData, { paymentLabels: paymentMethodLabelsQuipu, titleSuffix: 'Quipu' })}
              </div>
            </div>

            <div className="z-history-grid">
              <div className="z-card">
                <h3>Últimos movimientos (DB)</h3>
                <ul className="z-list z-history-list">
                  {dbHistory.map((m) => (
                    <li key={m.id}>
                      <span className="z-label">
                        #{m.id} · {paymentMethodLabelsDb[m.paymentMethod] || m.paymentMethod} · {activityLabels[m.activityType] || m.activityType}
                      </span>
                      <span className="z-amount">{Number(m.grossAmount || 0).toFixed(2)} €</span>
                    </li>
                  ))}
                  {dbHistory.length === 0 && <li className="z-empty">Sin movimientos</li>}
                </ul>
              </div>

              <div className="z-card">
                <h3>Últimos documentos (Quipu)</h3>
                <ul className="z-list z-history-list">
                  {quipuHistory.map((d) => (
                    <li key={`${d.type}-${d.id}`}>
                      <span className="z-label">
                        {d.type === 'tickets'
                          ? 'Ticket'
                          : (d.type === 'simplified_invoices' ? 'Simplificada' : 'Factura')}{' '}
                        {d.number ? `#${d.number}` : `id:${d.id}`}{' '}
                        {d.stage ? `· ${String(d.stage).toUpperCase()}` : ''} ·{' '}
                        {paymentMethodLabelsQuipu[d.payment_method] || d.payment_method}
                      </span>
                      <span className="z-amount">{d.total_amount != null ? `${d.total_amount} €` : '—'}</span>
                    </li>
                  ))}
                  {quipuHistory.length === 0 && <li className="z-empty">Sin documentos</li>}
                </ul>
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="stats-overview informes-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <p>Rentabilidad por actividad disponible en Z diaria</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <BarChart3 size={24} />
          </div>
          <div className="stat-content">
            <p>Movimientos y facturas en backend: <code>/admin/facturacion</code></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Informes;
